import { HasId, ITimestamps } from '@engineering11/types'

/**
 * Frontend contract types for the employer assistant confirmation card (Epic 4, story 4.8).
 *
 * The pending-action doc surfaces the exact frozen arguments of a consequential action the assistant
 * is about to take (which job, which person, which content) so the Plus user approves the specific
 * details rather than granting blanket trust. Backend-shaped values (the status string values, the
 * doc/args field names) are ASSUMPTIONS mirrored from the already-merged backend assistant epics. They
 * are confined to this file, the repository, the service and the `ASSISTANT_PENDING_ACTION` collection
 * constant. Confirm/replace them per BACKEND-CONTRACT.md.
 */

/**
 * BACKEND CONTRACT (assumption) — see ../BACKEND-CONTRACT.md
 * Pending-action lifecycle status. `AwaitingConfirmation` is the only confirmable state; `Expired` and
 * `Superseded` are the two withdrawn states (the action can no longer be confirmed); `Confirmed` means
 * the action was accepted and the card resolves (design-contract.md → §Surface 4 states).
 */
export enum PendingActionStatus {
  AwaitingConfirmation = 'awaiting_confirmation',
  Expired = 'expired',
  Superseded = 'superseded',
  Confirmed = 'confirmed',
}

/**
 * The exact frozen arguments the assistant will act on, displayed read-only (design-contract.md →
 * §Surface 4). Each is an optional display label — rendered only when present, never edited.
 * BACKEND CONTRACT (assumption) — see ../BACKEND-CONTRACT.md (field names: job, person, content)
 */
export interface IPendingActionArgs {
  /** e.g. the job title the action targets. */
  job?: string
  /** e.g. the candidate/person name the action targets. */
  person?: string
  /** e.g. the message/item content being acted on. */
  content?: string
}

/**
 * The live pending-action document backing the confirmation card. Keyed by conversation id
 * (doc id = conversation id → one active pending action per conversation).
 * BACKEND CONTRACT (assumption) — see ../BACKEND-CONTRACT.md (field names: status, stepId, args)
 */
export interface IAssistantPendingAction extends HasId<string>, Partial<ITimestamps> {
  status: PendingActionStatus
  /** Optional correlation to the run step that produced this action (carried for a future multi-action contract). */
  stepId?: string
  args: IPendingActionArgs
}

/**
 * The action can be confirmed only while awaiting confirmation. Pure function — unit-tested.
 * (design-contract.md → §Surface 4)
 */
export function isPendingActionConfirmable(status: PendingActionStatus | string): boolean {
  return status === PendingActionStatus.AwaitingConfirmation
}

/**
 * The action is withdrawn (no longer confirmable) when it has expired or been superseded. This is
 * distinct from a submit failure, which keeps the frozen args and re-enables Confirm. Pure function —
 * unit-tested. (design-contract.md → §Surface 4)
 */
export function isPendingActionWithdrawn(status: PendingActionStatus | string): boolean {
  return status === PendingActionStatus.Expired || status === PendingActionStatus.Superseded
}

/**
 * The English-as-key explanation shown when the action is withdrawn (design-contract.md → §Surface 4).
 * `Expired` and `Superseded` carry distinct copy; any other status returns an empty key (no explanation).
 */
export function withdrawnReasonKey(status: PendingActionStatus | string): string {
  switch (status) {
    case PendingActionStatus.Expired:
      return 'This action expired and can no longer be confirmed.'
    case PendingActionStatus.Superseded:
      return 'This action was replaced and can no longer be confirmed.'
    default:
      return ''
  }
}
